/**
 * Registers the strict drain handler invoked by the native UploadWorker
 * (BGProcessingTask) via the upload-drain bridge.
 *
 * The gate enforces, in order:
 *   1. There's a current trip (otherwise nothing to drain).
 *   2. canAutoUpload() — upload mode + Wi-Fi/captive-portal + battery rules.
 *
 * The actual offline upload queue isn't wired yet; once the gate passes this
 * handler just logs. The important part is that battery / Wi-Fi / mode rules
 * are honored *before* any future drain logic gets to run — never prompt
 * the user from a background wake.
 */
import { canAutoUpload } from "./network";
import { registerUploadDrainHandler } from "./upload-drain";
import { isNative } from "./platform";
import { liveActivityController } from "@/lib/live-activity/controller";
import { getCachedTrip } from "@/lib/offline/trip-cache";
import { getLocalUploadRules } from "@/lib/offline/upload-rules-local";

export function installStrictUploadDrainHandler(): void {
  if (!isNative()) return;

  registerUploadDrainHandler(async () => {
    let signaledUploading = false;
    try {
      // Local-first: prefer the cached trip + locally-stored upload rules so
      // background drains work offline and reflect the user's latest intent
      // even if the server hasn't caught up yet. Fall back to live server
      // reads only if local cache is missing (brand-new install).
      let tripId: string | null = (await getCachedTrip())?.id ?? null;
      if (!tripId) {
        try {
          const { getMyTrip } = await import("@/lib/trip.functions");
          const trip = await getMyTrip({});
          tripId = trip?.trip?.id ?? null;
        } catch (e) {
          console.info("[upload-drain] could not fetch trip (offline?), skipping", e);
          return;
        }
      }
      if (!tripId) {
        console.info("[upload-drain] no current trip, skipping");
        return;
      }

      const localRules = await getLocalUploadRules(tripId);
      const rules = {
        wifiOnly: localRules.wifiOnly,
        uploadMode: localRules.uploadMode,
        minBatteryPct: localRules.minBatteryPct,
        requireChargingUnderMin: localRules.requireChargingUnderMin,
      };

      const gate = await canAutoUpload({
        wifiOnlyPref: rules.wifiOnly ?? true,
        uploadMode: rules.uploadMode ?? "auto",
        minBatteryPct: rules.minBatteryPct ?? 20,
        requireChargingUnderMin: rules.requireChargingUnderMin ?? true,
      });

      if (!gate.ok) {
        console.info("[upload-drain] gate blocked:", gate.reason);
        if (typeof gate.reason === "string" && /unreachable|captive/i.test(gate.reason)) {
          console.warn("[connectivity] upload blocked reason=backend-unreachable");
        }
        // Treat blocked gate as paused if there's pending work; otherwise idle.
        try { liveActivityController.onUploadChanged("paused"); } catch {}
        return;
      }

      // Signal uploading start to the Live Activity controller.
      try { liveActivityController.onUploadChanged("uploading"); signaledUploading = true; } catch {}

      // TODO: when an offline upload queue exists, drain it here.
      console.info("[upload-drain] gate passed, nothing queued");
    } catch (err) {
      console.warn("[upload-drain] handler failed", err);
      try { liveActivityController.onUploadChanged("paused"); } catch {}
    } finally {
      if (signaledUploading) {
        try { liveActivityController.onUploadChanged("complete"); } catch {}
      }
    }
  });
}
